import { createContext, useContext, useEffect, useState } from "react";
import api, { setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=guest, object=user
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const ssoLogin = async (token) => {
    const { data } = await api.post("/integrations/rahal/sso", { token });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const rahalPasswordLogin = async (email, password) => {
    const { data } = await api.post("/integrations/rahal/sso/password", { email, password });
    setToken(data.access_token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setToken(null);
    setUser(false);
  };

  const refreshUser = async () => {
    const { data } = await api.get("/auth/me");
    setUser(data);
  };

  // Server-side permissions (RBAC): drives menu visibility; the API enforces the same keys.
  useEffect(() => {
    if (!user) { setPermissions([]); return; }
    api.get("/admin/my-permissions")
      .then((r) => setPermissions(r.data.permissions || []))
      .catch(() => setPermissions([]));
  }, [user]);

  const can = (key) => permissions.includes("*") || permissions.includes(key);

  return (
    <AuthContext.Provider value={{ user, loading, permissions, can, login, register, ssoLogin, rahalPasswordLogin, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
